import React, { LegacyRef } from 'react';

import { ReactComponent as CloseIcon } from '/src/images/icons/close.svg';

import { useIsMobile } from '../../hooks/use-is-mobile';
import { IContentfulImage } from '../../interfaces/contentful-image.interface';
import { ILibraryMeetingPlace } from '../../queries/fragments/libraryMeetingPlace';
import { ILibraryMenuGroup } from '../../queries/fragments/libraryMenuGroup';
import { ILibrarySocialMediaLinks } from '../../queries/fragments/librarySocialMediaLinks';
import { PageContext } from '../../templates/page-context.interface';
import { BlockSections } from '../blocks/block-sections';
import { RenderBlocks } from '../blocks/render-blocks';
import { ITextAndImageCardBlock } from '../blocks/text-and-image-card-block/text-and-image-card-block';
import { HeaderOpeningHours } from '../header/header-opening-hours';
import ImageWrapper from '../image-wrapper/Image-wrapper';
import { LanguageSelectorWidget } from '../language-selector/language-selector-widget';
import { SocialMediaLinksList } from '../social-media-links-list/social-media-links-list';
import LinkWrapper from '../ui/link-wrapper/link-wrapper';
import * as styles from './fold-out-menu.module.scss';
import { FoldOutMenuSubMenu } from './fold-out-menu-submenu';
interface FoldOutMenuProps {
  el: LegacyRef<HTMLDivElement> | undefined;
  toggleMenu: () => void;
  logo: IContentfulImage;
  menuGroups: ILibraryMenuGroup[];
  menuCards: ITextAndImageCardBlock[];
  socialMediaLinks: ILibrarySocialMediaLinks;
  meetingPlace: ILibraryMeetingPlace;
  menuClass: string;
  pageContext: PageContext;
}

export const FoldOutMenu = ({
  el,
  toggleMenu,
  logo,
  menuGroups,
  menuCards,
  socialMediaLinks,
  meetingPlace,
  menuClass,
  pageContext,
}: FoldOutMenuProps) => {
  const aspectRatio =
    logo?.gatsbyImageData?.width && logo?.gatsbyImageData?.height
      ? logo.gatsbyImageData.width / logo.gatsbyImageData.height
      : undefined;
  const isMobile = useIsMobile();
  if (isMobile) {
    menuCards = menuCards?.map((card) => {
      return { ...card, isMobileMenuCard: true };
    });
  }
  return (
    <div
      ref={el}
      id="main-menu"
      className={`fixed bottom-0 left-0 z-50 -mb-5 ml-[50%] w-screen -translate-x-1/2 rounded-t-xxl bg-brand-white shadow-default transition-all duration-300 ease-out lg:bottom-auto lg:top-0 lg:mb-auto lg:ml-0 lg:max-h-full lg:w-full lg:translate-x-0 lg:animate-fadeIn lg:overflow-y-auto lg:rounded-b-xxl lg:rounded-t-none ${menuClass}`}
    >
      <button
        className={
          'fixed -top-20 right-4 z-40 grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all hover:bg-brand-grey-2 focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:right-6 lg:top-6 lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark'
        }
        onClick={toggleMenu}
      >
        <CloseIcon />
        <span className="sr-only">Close menu</span>
      </button>

      <div className="max-lg:mobilemenuheight scrollbar-mt relative overflow-y-auto rounded-b-xxl px-6 text-neutral-grey-900 xl:container max-lg:rounded-t-xxl">
        <div className="mt-5 lg:mt-0 lg:hidden">
          <LinkWrapper data={meetingPlace?.openingHoursLink}>
            <HeaderOpeningHours meetingPlace={meetingPlace} />
          </LinkWrapper>
        </div>
        <div className="relative ml-6 mt-12 hidden lg:block xl:ml-28">
          {logo && (
            <LinkWrapper data={`/${meetingPlace.node_locale}`}>
              <ImageWrapper
                alt={logo.description ? logo.description : ''}
                file={logo.file}
                gatsbyImageData={logo.gatsbyImageData}
                svg={logo.svg}
                className={aspectRatio == 1 ? 'max-h-15 w-36' : 'max-h-10 w-36'}
                imgClassName={
                  aspectRatio == 1 ? 'max-h-15 !w-auto' : 'max-h-10 !w-auto'
                }
              />
            </LinkWrapper>
          )}
        </div>
        <div
          className={
            'mt-5 lg:mt-12 lg:flex lg:justify-between lg:gap-5 lg:px-26 xl:px-44'
          }
        >
          {menuGroups?.map((menuGroup, index) => {
            return (
              <FoldOutMenuSubMenu
                key={`${menuGroup.title}-${index}`}
                id={index.toString()}
                menuGroup={menuGroup}
                isMobile={isMobile}
              />
            );
          })}
        </div>
        {meetingPlace.showLanguageSelector && (
          <LanguageSelectorWidget
            node_locale={meetingPlace.node_locale}
            languageSelectorClass="lg:fixed lg:right-7 top-68 my-9 lg:hidden"
            variant="compact"
            hrefLangTags={pageContext.hrefLangTags}
            primaryLanguage={meetingPlace.primaryLanguage}
            secondaryLanguages={meetingPlace.languages ?? []}
          />
        )}
        <div className={styles.cardHolder}>
          <RenderBlocks blocks={menuCards as unknown as BlockSections[]} />
        </div>
        <div className="mt-16 grid gap-5 lg:grid-cols-2 lg:px-26 xl:px-44">
          <div className="order-2 lg:order-1">
            <p className="text-center text-bodys text-brand-grey-3 lg:text-left">
              © Ingka Centres
              <br />
              2010-{new Date().getFullYear()}
            </p>
          </div>
          <div className="order-1 lg:order-2">
            <SocialMediaLinksList socialMediaLinks={socialMediaLinks} />
          </div>
        </div>
      </div>
    </div>
  );
};
